import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

interface HighlightStep {
  id: string;
  targetSelector: string;
  title: string;
  description: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
}

interface GuidedHighlightProps {
  steps: HighlightStep[];
  isActive: boolean;
  onComplete: () => void;
  onDismiss: () => void;
  currentStep?: number;
  onStepChange?: (step: number) => void;
}

const GuidedHighlight: React.FC<GuidedHighlightProps> = ({
  steps,
  isActive,
  onComplete,
  onDismiss,
  currentStep = 0,
  onStepChange,
}) => {
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [highlightStyle, setHighlightStyle] = useState<React.CSSProperties>({});
  const [isVisible, setIsVisible] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);

  const step = steps[currentStep];

  const updatePosition = useCallback(() => {
    if (!isActive || !step) return;

    const target = document.querySelector(step.targetSelector);
    if (target) {
      const rect = target.getBoundingClientRect();
      const padding = 6;

      // Position the highlight ring around the element
      setHighlightStyle({
        position: 'fixed',
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
        pointerEvents: 'none',
        zIndex: 9998,
      });

      // Calculate tooltip position
      const tooltipWidth = 280;
      const tooltipHeight = tooltipRef.current?.offsetHeight || 120;
      const margin = 12;

      let top = rect.bottom + margin;
      let left = rect.left + rect.width / 2 - tooltipWidth / 2;

      // Determine best position
      const position = step.position || 'auto';
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      if (position === 'auto') {
        // Check space below
        if (rect.bottom + tooltipHeight + margin > viewportHeight) {
          // Not enough space below, try above
          if (rect.top - tooltipHeight - margin > 0) {
            top = rect.top - tooltipHeight - margin;
          } else {
            // Try right
            top = rect.top;
            left = rect.right + margin;
            if (left + tooltipWidth > viewportWidth) {
              // Try left
              left = rect.left - tooltipWidth - margin;
            }
          }
        }
      } else if (position === 'top') {
        top = rect.top - tooltipHeight - margin;
      } else if (position === 'left') {
        top = rect.top;
        left = rect.left - tooltipWidth - margin;
      } else if (position === 'right') {
        top = rect.top;
        left = rect.right + margin;
      }

      // Clamp to viewport
      if (left < margin) left = margin;
      if (left + tooltipWidth > viewportWidth - margin) {
        left = viewportWidth - tooltipWidth - margin;
      }
      if (top < margin) top = margin;

      setTooltipPosition({ top, left });
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [isActive, step]);

  useEffect(() => {
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    // Update periodically to catch dynamic elements
    const interval = setInterval(updatePosition, 300);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      clearInterval(interval);
    };
  }, [updatePosition]);

  // Listen for clicks on the highlighted element to auto-advance
  useEffect(() => {
    if (!isActive || !step) return;

    const handleClick = (e: MouseEvent) => {
      const target = document.querySelector(step.targetSelector);
      if (target && (target === e.target || target.contains(e.target as Node))) {
        // User clicked on the highlighted element
        if (currentStep < steps.length - 1) {
          // Advance to next step after a short delay
          setTimeout(() => {
            onStepChange?.(currentStep + 1);
          }, 500);
        } else {
          // Complete the tutorial
          setTimeout(() => {
            onComplete();
          }, 500);
        }
      }
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [isActive, step, currentStep, steps.length, onStepChange, onComplete]);

  if (!isActive || !step || !isVisible) return null;

  return (
    <>
      {/* Highlight ring - NO blocking overlay */}
      <Box
        ref={highlightRef}
        sx={{
          ...highlightStyle,
          borderRadius: '12px',
          boxShadow: '0 0 0 3px rgba(97, 123, 255, 0.5), 0 0 20px rgba(97, 123, 255, 0.3)',
          animation: 'guided-pulse 2s ease-in-out infinite',
          '@keyframes guided-pulse': {
            '0%': { 
              boxShadow: '0 0 0 3px rgba(97, 123, 255, 0.5), 0 0 20px rgba(97, 123, 255, 0.3)' 
            },
            '50%': { 
              boxShadow: '0 0 0 6px rgba(97, 123, 255, 0.3), 0 0 30px rgba(97, 123, 255, 0.4)' 
            },
            '100%': { 
              boxShadow: '0 0 0 3px rgba(97, 123, 255, 0.5), 0 0 20px rgba(97, 123, 255, 0.3)' 
            },
          },
        }}
      />

      {/* Small tooltip card */}
      <Box
        ref={tooltipRef}
        sx={{
          position: 'fixed',
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          width: 280,
          bgcolor: 'white',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
          border: '1px solid #E5E7EB',
          zIndex: 9999,
          overflow: 'hidden',
          transition: 'top 0.2s ease, left 0.2s ease, opacity 0.2s ease',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            bgcolor: '#F9FAFB',
            borderBottom: '1px solid #E5E7EB',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '6px',
                bgcolor: '#617BFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontSize: '12px', fontWeight: 700, color: 'white' }}>
                {currentStep + 1}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '12px', color: '#667085' }}>
              Step {currentStep + 1} of {steps.length}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={onDismiss}
            sx={{
              p: 0.5,
              color: '#9CA3AF',
              '&:hover': { color: '#667085', bgcolor: '#F3F4F6' },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#101828', mb: 0.5 }}>
            {step.title}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#667085', lineHeight: 1.5 }}>
            {step.description}
          </Typography>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderTop: '1px solid #E5E7EB',
          }}
        >
          <Typography
            onClick={onDismiss}
            sx={{
              fontSize: '12px',
              color: '#9CA3AF',
              cursor: 'pointer',
              '&:hover': { color: '#667085' },
            }}
          >
            Skip
          </Typography>

          {/* Progress dots */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {steps.map((_, idx) => (
              <Box
                key={idx}
                sx={{
                  width: idx === currentStep ? 16 : 6,
                  height: 6,
                  borderRadius: 3,
                  bgcolor: idx <= currentStep ? '#617BFF' : '#E5E7EB',
                  transition: 'all 0.2s ease',
                }}
              />
            ))}
          </Box>

          {currentStep < steps.length - 1 ? (
            <Box
              onClick={() => onStepChange?.(currentStep + 1)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: '#617BFF',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': { color: '#4A67E3' },
              }}
            >
              Next
              <ArrowForwardIcon sx={{ fontSize: 14 }} />
            </Box>
          ) : (
            <Box
              onClick={onComplete}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: '#10B981',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': { color: '#059669' },
              }}
            >
              Done
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default GuidedHighlight;
